import { makeAutoObservable } from "mobx";
import RootStore from "../../../../../stores/RootStore";
import ServiceStore from "../../../../../stores/ServiceStore";
import {
  Opportunity,
  Account,
  Contact
} from "types/common/ServiceStoreTypes";
import { OpportunityFormData } from "../../../types/Opportunity.types";

interface ValidationErrors {
  opportunityName?: string;
  accountId?: string;
  estimatedValue?: string;
  winProbability?: string;
  expectedCloseDate?: string;
  salesStage?: string;
}

export class OpportunityFormStore {
  rootStore: RootStore;

  // Observables - Only form UI state
  formData: OpportunityFormData = {
    accountId: undefined,
    contactId: undefined,
    opportunityName: "",
    salesStage: "Prospecting",
    opportunityType: "",
    leadSource: "",
    estimatedValue: 0,
    winProbability: 0,
    expectedCloseDate: "",
    description: "",
    competitorAnalysis: "",
    nextSteps: "",
  };

  originalData: OpportunityFormData | null = null;
  isEditing = false;
  opportunityId: number | null = null;
  error: string | null = null;
  validationErrors: ValidationErrors = {};
  isDirty = false;

  // Observable loading states
  loading = false;
  saving = false;
  loadingAccounts = false;
  loadingContacts = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Service store access
  get opportunitiesStore(): ServiceStore {
    return this.rootStore.opportunitiesStore as ServiceStore;
  }

  get accountsStore(): ServiceStore {
    return this.rootStore.accountsStore as ServiceStore;
  }

  get contactsStore(): ServiceStore {
    return this.rootStore.contactsStore as ServiceStore;
  }

  // Form validation
  get isValid(): boolean {
    this.validateForm();
    return Object.keys(this.validationErrors).length === 0;
  }

  get hasChanges(): boolean {
    if (!this.originalData) return this.isDirty;
    return JSON.stringify(this.formData) !== JSON.stringify(this.originalData);
  }

  // Form management methods
  async initializeForm(opportunityId?: string) {
    this.loading = true;
    this.error = null;

    try {
      // Load available accounts and contacts
      await Promise.all([
        this.loadAvailableAccounts(),
        this.loadAvailableContacts(),
      ]);

      if (opportunityId) {
        this.isEditing = true;
        this.opportunityId = parseInt(opportunityId);
        await this.loadOpportunity(this.opportunityId);
      } else {
        this.isEditing = false;
        this.opportunityId = null;
        this.resetForm();
      }
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to initialize form";
    } finally {
      this.loading = false;
    }
  }

  private async loadOpportunity(id: number) {
    try {
      const result = await this.opportunitiesStore.find({
        query: { opportunityId: id },
      });
      const opportunity = result?.data?.[0] as unknown as Opportunity;
      if (opportunity) {
        this.formData = {
          accountId: opportunity.accountId,
          contactId: opportunity.contactId,
          opportunityName: opportunity.opportunityName,
          salesStage: opportunity.salesStage,
          opportunityType: opportunity.opportunityType || "",
          leadSource: opportunity.leadSource || "",
          estimatedValue: opportunity.estimatedValue || 0,
          winProbability: opportunity.winProbability || 0,
          expectedCloseDate: opportunity.expectedCloseDate || "",
          description: opportunity.description || "",
          competitorAnalysis: opportunity.competitorAnalysis || "",
          nextSteps: opportunity.nextSteps || "",
        };
        this.originalData = { ...this.formData };

        // Load contacts for the selected account
        if (opportunity.accountId) {
          // Contacts will be available through computed property
        }
      }
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to load opportunity";
    }
  }

  private async loadAvailableAccounts() {
    this.loadingAccounts = true;
    try {
      await this.accountsStore.find();
    } catch (error) {
      console.error("Failed to load accounts:", error);
    } finally {
      this.loadingAccounts = false;
    }
  }

  private async loadAvailableContacts() {
    this.loadingContacts = true;
    try {
      await this.contactsStore.find();
    } catch (error) {
      console.error("Failed to load contacts:", error);
    } finally {
      this.loadingContacts = false;
    }
  }

  // Form field updates
  updateField = <K extends keyof OpportunityFormData>(
    field: K,
    value: OpportunityFormData[K]
  ) => {
    this.formData[field] = value;
    this.isDirty = true;
    this.clearFieldError(field as string);

    // Handle account change
    if (field === "accountId" && typeof value === "number" && value > 0) {
      // Reset contact selection when account changes if current contact is not in the new account
      if (
        this.formData.contactId &&
        !this.contactsForAccount.find(
          (c: Contact) => c.contactId === this.formData.contactId
        )
      ) {
        this.updateField("contactId", undefined);
      }
    }
  };

  private clearFieldError(field: string) {
    if (this.validationErrors[field as keyof ValidationErrors]) {
      delete this.validationErrors[field as keyof ValidationErrors];
    }
  }

  // Validation
  private validateForm() {
    const errors: ValidationErrors = {};

    if (!this.formData.opportunityName?.trim()) {
      errors.opportunityName = "Opportunity name is required";
    }

    if (!this.formData.accountId || this.formData.accountId <= 0) {
      errors.accountId = "Account is required";
    }

    if (this.formData.estimatedValue && this.formData.estimatedValue < 0) {
      errors.estimatedValue = "Estimated value cannot be negative";
    }

    if (
      this.formData.winProbability &&
      (this.formData.winProbability < 0 || this.formData.winProbability > 100)
    ) {
      errors.winProbability = "Win probability must be between 0 and 100";
    }

    if (!this.formData.expectedCloseDate?.trim()) {
      errors.expectedCloseDate = "Expected close date is required";
    }

    if (!this.formData.salesStage?.trim()) {
      errors.salesStage = "Sales stage is required";
    }

    this.validationErrors = errors;
  }

  // Form actions
  async saveOpportunity(): Promise<boolean> {
    if (!this.isValid) {
      return false;
    }

    this.saving = true;
    this.error = null;

    try {
      let result: Opportunity;

      if (this.isEditing && this.opportunityId) {
        result = (await this.opportunitiesStore.update(
          this.opportunityId,
          this.formData
        )) as unknown as Opportunity;
      } else {
        result = (await this.opportunitiesStore.create(
          this.formData
        )) as unknown as Opportunity;
        this.opportunityId = result.opportunityId;
      }

      this.originalData = { ...this.formData };
      this.isDirty = false;

      // Log activity
      try {
        const activityData = {
          entityType: "Opportunity",
          entityId: result.opportunityId,
          type: this.isEditing ? "Update" : "Create",
          description: `Opportunity ${this.isEditing ? "updated" : "created"
            }: ${result.opportunityName}`,
          metadata: {
            previousStage: this.isEditing
              ? this.originalData?.salesStage
              : null,
            currentStage: result.salesStage,
            estimatedValue: result.estimatedValue,
          },
        };

        const activitiesStore = this.rootStore.activitiesStore as ServiceStore;
        await activitiesStore?.create(activityData);
      } catch (activityError) {
        console.error("Failed to log activity:", activityError);
      }

      return true;
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to save opportunity";
      return false;
    } finally {
      this.saving = false;
    }
  }

  resetForm() {
    this.formData = {
      accountId: undefined,
      contactId: undefined,
      opportunityName: "",
      salesStage: "Prospecting",
      opportunityType: "",
      leadSource: "",
      estimatedValue: 0,
      winProbability: 0,
      expectedCloseDate: "",
      description: "",
      competitorAnalysis: "",
      nextSteps: "",
    };
    this.originalData = null;
    this.validationErrors = {};
    this.isDirty = false;
    this.error = null;
  }

  // Utility methods
  get availableAccounts(): Account[] {
    const accountsStore = this.rootStore.accountsStore as ServiceStore;
    return accountsStore.entities.map((account: unknown) => {
      return account as unknown as Account;
    });
  }

  get availableContacts(): Contact[] {
    const contactsStore = this.rootStore.contactsStore as ServiceStore;
    return contactsStore.entities.map((contact: unknown) => {
      return contact as unknown as Contact;
    });
  }

  get contactsForAccount(): Contact[] {
    if (!this.formData.accountId) return [];

    const contactsStore = this.rootStore.contactsStore as ServiceStore;
    return contactsStore.entities
      .filter((contact: unknown) => {
        const contactData = contact as { accountId?: number };
        return contactData.accountId === this.formData.accountId;
      })
      .map((contact: unknown) => contact as unknown as Contact);
  }

  get stageOptions() {
    return [
      "Prospecting",
      "Qualification",
      "Proposal",
      "Negotiation",
      "Closed Won",
      "Closed Lost",
    ];
  }

  get typeOptions() {
    return [
      "New Business",
      "Existing Customer - Upgrade",
      "Existing Customer - Replacement",
      "Existing Customer - Downgrade",
    ];
  }

  get leadSourceOptions() {
    return [
      "Website",
      "Referral",
      "Trade Show",
      "Cold Call",
      "Email Campaign",
      "Social Media",
      "Partner",
      "Other",
    ];
  }

  // Cleanup
  dispose() {
    // Clean up any subscriptions or timers if needed
  }
}

export default OpportunityFormStore;
